import {
  createContext,
  type Dispatch,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

import { ALL_FILTER_DEFINITIONS } from '../../filters/engine/filterRegistry';
import { FilterId, FilterType } from '../../filters/types/filters';
import { useNavigationGuard } from '../../hooks/useNavigationGuard';
import { PageExportProvider } from '../Chart/chart-menu/PageExportProvider';
import { useEditMode } from '../EditModeProvider/EditModeProvider';
import { usePageManager } from '../PageManagerProvider/PageManagerProvider';
import { usePageState } from '../PageState/PageStateProvider';
import {
  type PageAction,
  pageReducer,
  type TabContent,
  type TabContentAction,
} from './page-reducer';
import type { Page } from './types';
import { useReducerWithHistory } from './useReducerWithHistory';

type PageContextType = {
  config: Page;
  dispatch: Dispatch<PageAction>;
  saveChanges: () => Promise<boolean>;
  discardChanges: () => void;
  hasUnsavedChanges: boolean;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
};

const PageContext = createContext<PageContextType | undefined>(undefined);

/** Provides a no-op PageContext for read-only contexts (e.g. shared report viewer). */
export const StubPageConfigProvider = ({
  config,
  children,
}: {
  config: Page;
  children: ReactNode;
}) => {
  const stubValue: PageContextType = {
    config,
    dispatch: () => undefined,
    saveChanges: () => Promise.resolve(false),
    discardChanges: () => undefined,
    hasUnsavedChanges: false,
    undo: () => undefined,
    redo: () => undefined,
    canUndo: false,
    canRedo: false,
  };

  return (
    <PageContext.Provider value={stubValue}>{children}</PageContext.Provider>
  );
};

type ChildrenProps = {
  tabContent: TabContent;
};

type PageProviderProps = {
  initialConfig: Page;
  children: (props: ChildrenProps) => ReactNode;
};

export function PageProvider({ children, initialConfig }: PageProviderProps) {
  const [originalConfig, setOriginalConfig] = useState<Page>(initialConfig);

  const {
    state: draftConfig,
    dispatch: dispatchDraft,
    undo: internalUndo,
    redo: internalRedo,
    canUndo,
    canRedo,
    historyIndex,
  } = useReducerWithHistory<Page, PageAction>(pageReducer, initialConfig);

  const [lastSavedIndex, setLastSavedIndex] = useState<number>(historyIndex);

  const undo = useCallback(() => {
    internalUndo();
  }, [internalUndo]);

  const redo = useCallback(() => {
    internalRedo();
  }, [internalRedo]);

  const { editMode } = useEditMode();
  const { createOrUpdatePage } = usePageManager();
  const { pageState } = usePageState();
  const activeTabId = pageState.activeTabId;

  //allows us to dispatch actions to the draft config in edit mode, and to the original config in view mode
  const dispatch: Dispatch<PageAction> = useCallback(
    (action) => {
      if (editMode) {
        dispatchDraft(action);
      } else {
        setOriginalConfig((prev) => pageReducer(prev, action));
      }
    },
    [editMode, dispatchDraft]
  );

  const config = editMode ? draftConfig : originalConfig;

  const activeTab =
    config?.tabs.find((t) => t.id === activeTabId) ??
    config?.tabs.find((t) => t.isActive) ??
    config?.tabs?.[0];

  const hasUnsavedChanges = historyIndex !== lastSavedIndex;

  const discardChanges = useCallback(() => {
    dispatchDraft({ type: 'SET_CONFIG', payload: originalConfig });
  }, [dispatchDraft, originalConfig]);

  const saveChanges = async (): Promise<boolean> => {
    if (!draftConfig) return false;
    const saved = await createOrUpdatePage(draftConfig);
    if (saved) {
      setOriginalConfig(saved);
      setLastSavedIndex(historyIndex);
      // Return a promise that resolves after state updates
      return new Promise<boolean>((resolve) => {
        // Wait for the next render cycle to have hasUnsavedChanges updated
        setTimeout(() => resolve(true), 0);
      });
    }

    return false;
  };

  // Attach navigation guard only when in edit mode
  useNavigationGuard(editMode && hasUnsavedChanges, discardChanges);

  return (
    <PageContext.Provider
      value={{
        config,
        dispatch,
        saveChanges,
        discardChanges,
        hasUnsavedChanges,
        undo,
        redo,
        canUndo,
        canRedo,
      }}
    >
      <PageExportProvider>
        <TabContentProvider key={activeTab?.id}>{children}</TabContentProvider>
      </PageExportProvider>
    </PageContext.Provider>
  );
}

export const usePageConfig = () => {
  const context = useContext(PageContext);
  if (context === undefined) {
    throw new Error('usePageConfig must be used within a PageProvider');
  }
  return {
    config: context.config,
    dispatch: context.dispatch,
    saveChanges: context.saveChanges,
    discardChanges: context.discardChanges,
    hasUnsavedChanges: context.hasUnsavedChanges,
    undo: context.undo,
    redo: context.redo,
    canUndo: context.canUndo,
    canRedo: context.canRedo,
  };
};

const TabContentContext = createContext<
  { config: TabContent; dispatch: Dispatch<TabContentAction> } | undefined
>(undefined);

const TabContentProvider = ({
  children,
}: {
  children: (props: { tabContent: TabContent }) => ReactNode;
}) => {
  const pageContext = useContext(PageContext);
  if (!pageContext) {
    throw new Error('TabContentProvider must be used within a PageProvider');
  }

  const { pageState } = usePageState();
  const activeTabId = pageState.activeTabId;

  const activeTab =
    pageContext.config.tabs.find((t) => t.id === activeTabId) ??
    pageContext.config.tabs.find((t) => t.isActive) ??
    pageContext.config.tabs[0];

  const dispatch = useCallback(
    (action: TabContentAction) => {
      pageContext.dispatch({
        type: 'APPLY_TAB_CONTENT_ACTION',
        payload: { tabId: activeTab?.id ?? '', action },
        ...(action.replacePreviousHistorySnapshot
          ? { replacePreviousHistorySnapshot: true }
          : {}),
      });
    },
    [pageContext, activeTab?.id]
  );

  if (!activeTab) return null;

  return (
    <TabContentContext.Provider
      value={{ config: activeTab.tabContent, dispatch }}
    >
      {children({ tabContent: activeTab.tabContent })}
    </TabContentContext.Provider>
  );
};

/** Provides a no-op TabContentContext for read-only contexts (e.g. shared report viewer). */
export const StubTabContentProvider = ({
  tabContent,
  children,
}: {
  tabContent: TabContent;
  children: ReactNode;
}) => {
  return (
    <TabContentContext.Provider
      value={{ config: tabContent, dispatch: () => undefined }}
    >
      {children}
    </TabContentContext.Provider>
  );
};

export function useTabContentConfig() {
  const tabContentContext = useContext(TabContentContext);
  const parentPageContext = useContext(PageContext);
  const revelioPageId = parentPageContext?.config?.revelioPageId;

  if (tabContentContext === undefined) {
    throw new Error(
      'useTabContentConfig must be used within a TabContentProvider'
    );
  }

  const primaryFilter = tabContentContext.config.primaryFilter;

  const hasPrimaryFilter = (filterId: FilterId) =>
    primaryFilter ? primaryFilter.id === filterId : false;

  const secondaryFilters = tabContentContext?.config?.secondaryFilters;
  const hasSecondaryFilter = (filterId: FilterId) => {
    if (secondaryFilters.length === 0) {
      return true;
    }

    // Flatten secondaryFilters: expand GROUP filters to their children,
    // and MULTI segment filters to include their wrapped id so single-segment
    // AI filters (e.g. HEADCOUNT) are considered on-page when MULTI_HEADCOUNT is in the template.
    const flattenedSecondaryFilterIds = secondaryFilters.flatMap(
      (secondaryFilter) => {
        const filterDef = ALL_FILTER_DEFINITIONS[secondaryFilter.id];
        if (filterDef?.type === FilterType.GROUP && filterDef.children) {
          return filterDef.children;
        }
        if (
          filterDef?.type === FilterType.MULTI &&
          'wrappedFilterId' in filterDef &&
          filterDef.wrappedFilterId
        ) {
          return [secondaryFilter.id, filterDef.wrappedFilterId];
        }
        return [secondaryFilter.id];
      }
    );

    return flattenedSecondaryFilterIds.includes(filterId);
  };

  const hasFilter = (filterId: FilterId) =>
    hasPrimaryFilter(filterId) || hasSecondaryFilter(filterId);

  const dateFilterId: FilterId.DATE_RANGE | FilterId.SNAPSHOT_DATE =
    hasSecondaryFilter(FilterId.DATE_RANGE)
      ? FilterId.DATE_RANGE
      : FilterId.SNAPSHOT_DATE;

  // Remove Revelio-only unofficial template options
  const rawConfig = tabContentContext?.config;
  let config = rawConfig;
  if (rawConfig && !revelioPageId) {
    config = { ...rawConfig, dateRangeDefault: undefined };
  }

  return {
    config,
    primaryFilter,
    parentId: parentPageContext?.config?.id,
    revelioPageId,
    dispatch: tabContentContext?.dispatch,
    hasFilter,
    hasSecondaryFilter,
    dateFilterId,
  };
}
